"use client";

import { useNotification } from "src/components/Notifications";
import { useSessionContext } from "src/contexts/SessionContext";
import { Link } from "@zapier/design-system";
import { useTranslations } from "next-intl";
import {
  TemplateBookmarkProvider as BaseTemplateBookmarkProvider,
  useTemplateBookmarkContext as useBaseTemplateBookmarkContext,
} from "@zapier/templates";
import type { TemplateBookmarkContextValue } from "@zapier/templates";
import { type ReactNode, useCallback } from "react";

/**
 * Service-layer wrapper for TemplateBookmarkProvider from @zapier/templates.
 *
 * This wrapper adds notification integration (toasts) for bookmark operations
 * while delegating all bookmark logic to the base provider from @zapier/templates.
 * It also automatically provides session state to skip unnecessary API calls
 * when the user is not logged in.
 *
 * @example
 * ```tsx
 * <TemplateBookmarkProvider>
 *   <YourComponent />
 * </TemplateBookmarkProvider>
 * ```
 */
export const TemplateBookmarkProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { session } = useSessionContext();
  const { createNotification, clearNotification } = useNotification();
  const t = useTranslations("BookmarkNotifications");

  const handleSuccess = useCallback(
    (action: "create" | "delete" | "fetch", _templateId?: string) => {
      if (action === "create") {
        createNotification({
          id: "template-bookmark",
          onDismiss: () => {
            clearNotification("template-bookmark");
          },
          content: (
            <>
              <strong>{t("savedLabel")} </strong>
              <Link href="/templates/saved">{t("viewSavedTemplates")}</Link>
            </>
          ),
          variant: "toast",
        });
      }
      // Note: We don't show notifications for delete operations
      // to keep the UX clean (removal is obvious from UI change)
    },
    [clearNotification, createNotification, t],
  );

  const handleError = useCallback(
    (
      _error: Error,
      action: "create" | "delete" | "fetch",
      _templateId?: string,
    ) => {
      const actionLabel =
        action === "create"
          ? t("errorSavingTemplate")
          : action === "delete"
            ? t("errorRemovingTemplate")
            : t("errorLoadingTemplates");
      createNotification({
        id: "template-bookmark",
        onDismiss: () => {
          clearNotification("template-bookmark");
        },
        content: <p>{actionLabel}</p>,
        variant: "toast",
        status: "error",
      });
    },
    [clearNotification, createNotification, t],
  );

  return (
    <BaseTemplateBookmarkProvider
      isLoggedIn={session.is_logged_in}
      onSuccess={handleSuccess}
      onError={handleError}
    >
      {children}
    </BaseTemplateBookmarkProvider>
  );
};

// Service-specific hook that enforces provider usage
export const useTemplateBookmarkContext = (): TemplateBookmarkContextValue => {
  const context = useBaseTemplateBookmarkContext();

  if (!context) {
    throw new Error(
      "useTemplateBookmarkContext must be used within TemplateBookmarkProvider",
    );
  }

  return context;
};
